import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGql, formatDateTime } from '@ah/design';
import { Header, DetailSkeleton, PullToRefresh } from '@ah/design/mobile';
import { ExternalLink, Clock, Podcast } from 'lucide-react';
import { PODCAST_SERIES_DETAIL_QUERY, type SeriesDetail } from '../shared/index.ts';
import { formatDuration, ErrorRetry } from './_parts.tsx';

export function PodcastSeries() {
  const gql = useGql();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['podcasts', 'series', id],
    queryFn: () => gql.request<{ podcastSeriesById: SeriesDetail | null }>(PODCAST_SERIES_DETAIL_QUERY, { id }),
  });

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title={data?.podcastSeriesById?.name ?? 'Podcast'} backTo="/podcasts/shows" />
      <PullToRefresh className="flex-1" onRefresh={() => refetch()}>
        {isLoading ? (
          <DetailSkeleton hero="square" />
        ) : error || !data?.podcastSeriesById ? (
          <ErrorRetry label="Podcast not found." onRetry={() => refetch()} />
        ) : (
          <SeriesBody s={data.podcastSeriesById} />
        )}
      </PullToRefresh>
    </div>
  );
}

function SeriesBody({ s }: { s: SeriesDetail }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <article className="px-4 pb-10 space-y-5">
      <div className="flex justify-center">
        {s.imageUrl ? (
          <img
            src={s.imageUrl}
            alt=""
            className="w-40 h-40 rounded-xl object-cover bg-muted"
          />
        ) : (
          <div className="w-40 h-40 rounded-xl bg-muted grid place-items-center text-muted-foreground opacity-40">
            <Podcast size={40} />
          </div>
        )}
      </div>

      <header className="space-y-2 text-center">
        <h1 className="text-xl font-bold leading-tight">{s.name}</h1>
        {s.author ? <p className="text-sm text-muted-foreground">{s.author}</p> : null}
        <div className="flex flex-wrap gap-1.5 justify-center">
          {s.genres.slice(0, 4).map((g) => (
            <span
              key={g}
              className="text-[10px] uppercase tracking-wide rounded-full border px-2 py-0.5 text-muted-foreground"
            >
              {g.replace(/^PODCASTSERIES_/, '').replace(/_/g, ' ').toLowerCase()}
            </span>
          ))}
        </div>
        <div className="text-xs text-muted-foreground flex flex-wrap items-center justify-center gap-3">
          {s.totalEpisodesCount ? <span>{s.totalEpisodesCount} episodes</span> : null}
          {s.popularityRankGlobal ? <span>Global rank #{s.popularityRankGlobal}</span> : null}
          {s.websiteUrl ? (
            <a
              href={s.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1"
            >
              <ExternalLink size={11} /> Website
            </a>
          ) : null}
        </div>
      </header>

      {s.description ? (
        <div>
          <p className={`text-sm leading-relaxed text-muted-foreground ${expanded ? '' : 'line-clamp-3'}`}>
            {s.description}
          </p>
          {s.description.length > 180 ? (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="text-xs text-primary mt-1"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          ) : null}
        </div>
      ) : null}

      <section>
        <h2 className="text-lg font-semibold mb-2">Latest episodes</h2>
        {s.latestEpisodes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No episodes ingested yet.</p>
        ) : (
          <div className="space-y-3">
            {s.latestEpisodes.map((ep) => {
              const image = ep.imageUrl ?? s.imageUrl ?? null;
              const duration = formatDuration(ep.duration);
              const content = (
                <div className="flex gap-3 p-3 bg-card rounded-xl border border-border">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-muted shrink-0">
                    {image ? (
                      <img src={image} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium line-clamp-2">{ep.name}</p>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1 flex-wrap">
                      {duration ? <span>{duration}</span> : null}
                      {ep.datePublished ? (
                        <span className="inline-flex items-center gap-1">
                          <Clock size={11} /> {formatDateTime(ep.datePublished)}
                        </span>
                      ) : null}
                      {ep.summaryGeneratedAt ? (
                        <span className="text-[9px] uppercase tracking-wide rounded-full border px-1.5 py-0.5">
                          Summary
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
              return ep.summaryGeneratedAt ? (
                <Link key={ep.id} to={`/podcasts/${ep.id}`} className="block">
                  {content}
                </Link>
              ) : (
                <div key={ep.id}>{content}</div>
              );
            })}
          </div>
        )}
      </section>
    </article>
  );
}
